import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {DialogActions, DialogContent, Divider, SvgIcon} from "@mui/material";
import {Col, Row} from "reactstrap";
import Button from "../../../../../base/button/button";
import {ReactComponent as RemoveIcon} from "../../../../../../../assets/images/remove.svg";
import IconButton from "../../../../../base/button/icon_button";
import {OutlinedDropdown} from "../../../../../base/dropdown/outlined_dropdown";
import {dataGridCellTextProps, DataGridColumnAlignments, DataGridColumnTypes, DataGridColumnWidthTypes, useDataGridSavedState} from "../../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../../containers/data-grid";
import {ProductTechnicalFileAssignedItemsDataGridColumnNames} from "../../../../../../../core/constants/table/data-grid-column-keys";
import useIndustry from "../../../../../../hooks/use-industry";
import LoadingLottieAnimation from "../../../../../app-specific/lottie-animations/loading";
import ProductInformationPopover from "../../../../../app-specific/draggable-popover/product-information-popover";
import PartAutocomplete from "../../../../../text-fields/autocomplete/part-autocomplete";
import ImportPartsByCsv from "./import-parts-by-csv";

const dataGridSaveConfig = {
    name: 'AssignedItemsListProductsTabDataGrid',
    version: 2,
}

const AddAssignedItems = ({setInAddMode, assign, industryId}) => {
    const {t: tImportCsv} = useTranslation('components', {keyPrefix: 'app-specific.manage-assigned-items-add.importCsv'});
    const [selectedParts, setSelectedParts] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [assigning, setAssigning] = useState(false);
    const [showCsvImport, setShowCsvImport] = useState(false);

    const {
        cachedIndustry,
        industries,
        fetchIndustry,
        fetchedIndustry,
        categoriesOfFetchedIndustry: categories,
        hasNoIndustries,
        loadingIndustries
    } = useIndustry();

    const activeIndustryId = useMemo(
        () => industryId ?? fetchedIndustry?.id,
        [fetchedIndustry?.id, industryId],
    );

    const canAssign = useMemo(() => !!selectedCategory || !!selectedParts?.length, [selectedParts, selectedCategory])
    const existingPartIds = useMemo(() => selectedParts?.map(e => e.id), [selectedParts])
    const assignableCategories = useMemo(() => {
        return categories
            ?.filter(e => e.id !== selectedCategory?.id)
            ?.filter(e => !e.parentId)?.map(e => ({
                id: e.id,
                value: e,
                label: e.title ?? '',
            })) ?? [];
    }, [categories, selectedCategory?.id]);

    /**
     * Automatically fetches the cached industry when the component is mounted
     */
    useEffect(() => {
        const industry = industryId ?? cachedIndustry?.id;
        if (!industry)
            return;
        void fetchIndustry({industryId: industry});
    }, [cachedIndustry?.id, fetchIndustry, industryId]);

    /**
     * Assigns the selected items to the current technical file.
     *
     * * move the user back to the list view if the response is successful.
     * @return {Promise<void>}
     */
    const assignSelectedItems = async () => {
        setAssigning(true);
        const forApi = {
            records: selectedParts.map((part) => ({
                id: part.id,
                partNo: part.partNo ?? part.partNumber,
            })),
            category: selectedCategory,
        }
        const response = await assign(forApi);
        setAssigning(false);
        if (response?.resultFlag) {
            setInAddMode(false);
        }
    }

    /**
     * Adds the given parts to the selectedParts state if they are not already included.
     */
    const onPartsImported = useCallback((parts) => {
        setSelectedParts((prevState) => {
            const existingIds = prevState.map((e) => e.id);
            const newParts = parts.filter((part) => !existingIds.includes(part.id));
            return [...prevState, ...newParts];
        });
    }, []);

    /**
     * Adds the given item to the selectedItems state.
     * @param key
     * @param item
     */
    const addItem = (key, item) => {
        switch (key) {
            case "product":
                return setSelectedParts(prevState => {
                    const newPart = {...item, partNo: item.partNo ?? item.partNumber};
                    if (prevState?.map(e => e.id).includes(newPart.id))
                        return prevState;
                    return [...prevState, newPart];
                })
            case "category":
                return setSelectedCategory(item)
            default:
                return;
        }
    }

    /**
     * Removes the given item from the selectedItems state.
     * @param key
     * @param item
     */
    const removeItem = (key, item) => {
        switch (key) {
            case "product":
                return setSelectedParts(prevState => prevState.filter(e => e.id !== item.id))
            case "category":
                return setSelectedCategory(null)
            default:
                return;
        }
    }

    const columns = useMemo(() =>
        /** @return {DataGridColumn[]}*/
        [
            {
                title: 'Selected Products',
                name: ProductTechnicalFileAssignedItemsDataGridColumnNames.partNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: {
                    type: DataGridColumnWidthTypes.flex,
                    size: 1,
                },
            },
            {
                title: "Selected Products",
                name: ProductTechnicalFileAssignedItemsDataGridColumnNames.category,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: {
                    type: DataGridColumnWidthTypes.flex,
                    size: 1,
                },
            },
        ], [])

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig?.name,
        version: dataGridSaveConfig?.version,
    });

    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() => {
        const res = [];
        for (let i = 0; i < selectedParts.length; i += 2) {
            res.push([selectedParts[i], selectedParts[i + 1]]);
        }
        return res.map(([part1, part2]) => ({
            key: `${part1?.id}-${part2?.id}`,
            data: [part1, part2],
            cells: {
                [ProductTechnicalFileAssignedItemsDataGridColumnNames.partNo]:
                    part1
                        ? (
                            <div className={'d-flex align-items-center'}>
                                <IconButton
                                    onPressed={() => removeItem('product', part1)}
                                    disabled={assigning}
                                    color={'paper.error'}
                                    className={'mr-2'}
                                >
                                    <SvgIcon component={RemoveIcon} className='icon normal'/>
                                </IconButton>
                                <ProductInformationPopover
                                    partNo={part1.partNo}
                                    wrapperProps={{
                                        ...dataGridCellTextProps,
                                        className: 'text-sm black part-number-fluid'
                                    }}
                                >
                                    {part1.partNo ?? ''}
                                </ProductInformationPopover>
                            </div>
                        )
                        : <></>,
                [ProductTechnicalFileAssignedItemsDataGridColumnNames.category]:
                    part2
                        ? (
                            <div className={'d-flex align-items-center'}>
                                <IconButton
                                    onPressed={() => removeItem('product', part2)}
                                    disabled={assigning}
                                    color={'paper.error'}
                                    className={'mr-2'}
                                >
                                    <SvgIcon component={RemoveIcon} className='icon normal'/>
                                </IconButton>
                                <ProductInformationPopover
                                    partNo={part2.partNo}
                                    wrapperProps={{
                                        ...dataGridCellTextProps,
                                        className: 'text-sm black part-number-fluid'
                                    }}
                                >
                                    {part2.partNo ?? ''}
                                </ProductInformationPopover>
                            </div>
                        )
                        : <></>,
            },
        }));
    }, [assigning, selectedParts]);

    return (
        <>
            <DialogContent className={'px-4 py-2 pt-4'}>
                <div className={'d-flex flex-wrap'}>
                    {
                        loadingIndustries &&
                        <Col xs={12}>
                            <LoadingLottieAnimation width={'80%'}/>
                        </Col>
                    }
                    {
                        hasNoIndustries && !loadingIndustries &&
                        <p className={'text-sm grey-3 font-weight-semi-bold mb-3'}>
                            No industries found. Please add an industry or contact your administrator.
                        </p>
                    }
                    {
                        !showCsvImport &&
                        !loadingIndustries && !hasNoIndustries &&
                        <>
                            {
                                industries.length > 1 && !industryId &&
                                <Col xs={12}>
                                    <OutlinedDropdown
                                        fullWidth
                                        label={'Industry'}
                                        value={fetchedIndustry}
                                        onChange={(v) => fetchIndustry({industryId: v?.id}) || setSelectedCategory(null)}
                                        data={industries?.map(e => ({value: e, label: e.title, id: e.id})) ?? []}
                                        className={'mb-3'}
                                    />
                                </Col>
                            }
                            <Col xs={6}>
                                <p className={'text-md black font-weight-semi-bold mb-3'}>
                                    Assign by Part number
                                </p>
                                <PartAutocomplete
                                    industryId={industryId ?? activeIndustryId}
                                    clearOnBlur
                                    fullWidth
                                    label={'Search'}
                                    value={null}
                                    disabled={showCsvImport}
                                    onChange={(_, value) => value && addItem('product', value)}
                                    filterOptions={(options) => options.filter(e => !existingPartIds.includes(e.id))}
                                />
                                <Button
                                    variant={'outlined'}
                                    color={'primary'}
                                    size={'small'}
                                    className={'mt-2'}
                                    disabled={assigning || !activeIndustryId}
                                    onClick={() => setShowCsvImport((prev) => !prev)}
                                >
                                    {
                                        showCsvImport
                                            ? tImportCsv('cancelImportButton')
                                            : tImportCsv('importCsvButton')
                                    }
                                </Button>
                            </Col>
                            <Col xs={6} className={'d-flex flex-column'}>
                                <p className={'text-md black font-weight-semi-bold mb-3'}>
                                    Assign by category
                                </p>
                                <OutlinedDropdown
                                    fullWidth
                                    label={'Main Category'}
                                    value={selectedCategory ?? ''}
                                    onChange={(v) => addItem('category', v)}
                                    data={assignableCategories}
                                    className={'mb-3'}
                                />
                            </Col>
                        </>
                    }
                    {
                        showCsvImport &&
                        <ImportPartsByCsv
                            activeIndustryId={activeIndustryId}
                            existingPartIds={existingPartIds}
                            onPartsImported={onPartsImported}
                            onClose={() => setShowCsvImport(false)}
                        />
                    }
                    {
                        !showCsvImport &&
                        <PartnerDataGrid
                            {...dataGridSaveConfig}
                            hideFooter
                            hideExporting
                            initialLayoutState={{
                                columns: columns,
                            }}
                            rows={dataGridRows}
                            columns={dataGridColumns}
                            state={{
                                loading: {state: false},
                                density: dataGridDensity,
                            }}
                        />
                    }
                </div>
            </DialogContent>
            {
                !showCsvImport &&
                <DialogActions className={'p-0'}>
                    <Row className={'w-100 no-gutters'}>
                        <Divider className='divider'/>
                        <Row className='no-gutters justify-content-between align-items-start w-100'>
                            <Button
                                disabled={assigning}
                                variant={'dialog'}
                                color={'primary'}
                                onClick={() => setInAddMode(false)}
                                borderRadius={'0 8px 0 8px'}
                            >
                                Back to List
                            </Button>
                            <Button
                                disabled={assigning || !canAssign}
                                variant={'dialog'}
                                color={'primary'}
                                onClick={assignSelectedItems}
                                borderRadius={'8px 0 8px 0'}
                            >
                                {
                                    assigning
                                        ? "Assigning..."
                                        : "Assign"
                                }
                            </Button>
                        </Row>
                    </Row>
                </DialogActions>
            }
        </>
    )
}

export default AddAssignedItems;
